import { Component } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { map, distinctUntilChanged, filter } from 'rxjs/operators';
import { ReusableStreamProperty, shallowCompare } from '@ipreo/muni-shared';
import {
  Tranche,
  TrancheGroup,
  findTrancheById
} from '../../data/gameDay-data.transformations';
import { GameDayViewType } from '../../../state/data/state';
import { GameDayRootReducerState } from '../../../state/root.state';
import {
  getFullGamedayState,
  getGamedayViewTypeState,
  getGamedayLoadingState,
  getChartsState,
  getTrancheOrdersSearchedState
} from '../../../state/data/reducer';

enum GamedayDetailsViewType {
  SummaryDetails,
  TrancheDetails,
  EntityDetails
}

@Component({
  selector: 'muni-gameday-cards-holder',
  templateUrl: './cards-holder.component.html',
  styleUrls: ['./cards-holder.component.scss']
})
export class CardsHolderComponent {
  public readonly detailTypes: typeof GamedayDetailsViewType = GamedayDetailsViewType;

  @ReusableStreamProperty
  public readonly viewType: Observable<GameDayViewType> = this.store.pipe(
    select(getGamedayViewTypeState)
  );

  @ReusableStreamProperty
  public readonly isLoading: Observable<boolean> = this.store.pipe(
    select(getGamedayLoadingState)
  );

  @ReusableStreamProperty
  public readonly activeDetails: Observable<
    GamedayDetailsViewType
  > = this.store.pipe(
    select(getFullGamedayState),
    map(
      ({
        data,
        data: {
          filters: { searchByAccountManager }
        },
        charts: { selectedTrancheId },
        negativeCases,
        tranches
      }) => {
        if (!data.deal || negativeCases.chart) {
          return GamedayDetailsViewType.SummaryDetails;
        }

        const selectedTrancheIdPresentInData = tranches.searched.trancheGroups.some(
          (trancheGroup: TrancheGroup) => {
            return (
              trancheGroup.id === selectedTrancheId ||
              trancheGroup.tranches.some(
                tranche => tranche.id === selectedTrancheId
              )
            );
          }
        );

        if (selectedTrancheId !== undefined && selectedTrancheIdPresentInData) {
          return GamedayDetailsViewType.TrancheDetails;
        } else if (searchByAccountManager !== undefined) {
          return GamedayDetailsViewType.EntityDetails;
        }

        return GamedayDetailsViewType.SummaryDetails;
      }
    ),
    distinctUntilChanged()
  );

  private readonly selectedTrancheId: Observable<
    string | undefined
  > = this.store.pipe(
    select(getChartsState),
    map(({ selectedTrancheId }) => selectedTrancheId),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly selectedTranche: Observable<
    Tranche | undefined
  > = combineLatest(
    this.store.pipe(select(getTrancheOrdersSearchedState)),
    this.selectedTrancheId
  ).pipe(
    map(([{ trancheGroups }, selectedTrancheId]) =>
      selectedTrancheId && trancheGroups
        ? { trancheGroups, selectedTrancheId }
        : undefined
    ),
    filter(state => !!state),
    distinctUntilChanged(shallowCompare),
    map(({ selectedTrancheId, trancheGroups }) =>
      findTrancheById(trancheGroups, selectedTrancheId)
    ),
    filter(tranche => !!tranche),
    distinctUntilChanged(this.isSelectedTrancheChanged)
  );

  constructor(private store: Store<GameDayRootReducerState>) {}

  private isSelectedTrancheChanged(
    tranche1: Tranche,
    tranche2: Tranche
  ): boolean {
    return (
      tranche1.id === tranche2.id &&
      tranche1.parts === tranche2.parts &&
      shallowCompare(tranche1.orders, tranche2.orders) &&
      shallowCompare(tranche1.maturities, tranche2.maturities)
    );
  }
}
